/**
 * 审批中心纯函数核心（APC · corporate-api §4.9 / PAGE-MCH-USER-015）。
 *
 * 拆分先例：corporateCore.ts —— ./client 因 import.meta.env 顶层求值无法在 node 直跑导入，
 * 凡需可执行断言的纯逻辑一律收敛到本文件；approvals.ts 仅做请求薄层
 * （走 merchantGet/merchantPost 唯一边界）。本文件会被 node --experimental-strip-types
 * 直测导入，故对本地模块的 import 一律带显式 .ts 后缀（allowImportingTsExtensions）。
 *
 * 契约真源：
 * - docs/PRD/allinks/backend/_shared/corporate-api.md §4.9（API-CORP-APC-001/002）
 * - sprint-contract.md §3 裁决：D1（CORP_CARD_ISSUE 恒空集）/ D5（expiresIn 剩余秒数字符串）
 *   / D6（items 追加 corpId/sourceId）/ D7（KYC 决策唯一基准 API-KYC-008）/ D8（响应超集容错）
 *   / D9（Number(expiresIn) <= 300 红色高亮，含 "300" 边界）
 */
import { isForbiddenError } from './corporateCore.ts';

// ================= 类型枚举（对齐后端 ApprovalType 字面量） =================

/** 五类审批类型；CORP_CARD_ISSUE 保留契约键但本 Sprint 恒空集（D1，禁伪造数据） */
export const APPROVAL_TYPES = [
  'CORP_TX_APPROVAL',
  'CORP_CARD_ISSUE',
  'CORP_EXPENSE',
  'ONBOARDING',
  'KYC',
] as const;

export type ApprovalType = (typeof APPROVAL_TYPES)[number];

/** 运行时收窄（筛选参数 / 后端返回值防御；非法值返回 null 由调用方兜底） */
export function asApprovalType(raw: unknown): ApprovalType | null {
  const u = String(raw ?? '').trim().toUpperCase();
  return (APPROVAL_TYPES as readonly string[]).includes(u) ? (u as ApprovalType) : null;
}

/** 类型 → 中文标签（PRD pages.md §2.13；ONBOARDING 语义以 D3 企业 KYB 审核为准，取中性「开户审核」） */
export const APPROVAL_TYPE_LABEL: Record<ApprovalType, string> = {
  CORP_TX_APPROVAL: '企业消费审批',
  CORP_CARD_ISSUE: '企业开卡审批',
  CORP_EXPENSE: 'invoice 报销审核',
  ONBOARDING: '开户审核',
  KYC: 'KYC 审核',
};

export function mapApprovalTypeToUi(approvalType: string | null | undefined): string {
  const t = asApprovalType(approvalType);
  return t ? APPROVAL_TYPE_LABEL[t] : approvalType || '—';
}

// ================= APC-001 查询构建 =================

/** APC-001 列表 query（approvalType 逗号多值 / from / to / page / pageSize；空值不拼入）。 */
export type ApprovalCenterListQuery = {
  /** 五枚举子集；空数组 = 全部（不拼参数，后端缺省即全部） */
  approvalType?: string[];
  /** 提交时间区间（YYYY-MM-DD，各域单据创建时间） */
  from?: string;
  to?: string;
  page?: number;
  pageSize?: number;
};

export function buildPendingQuery(query: ApprovalCenterListQuery = {}): string {
  const params: string[] = [];
  const types = (query.approvalType ?? [])
    .map((t) => String(t).trim().toUpperCase())
    .filter((t) => t && (APPROVAL_TYPES as readonly string[]).includes(t));
  // 去重保序（后端 E2 口径对重复/空段容错，前端不产出脏值）
  const joined = Array.from(new Set(types)).join(',');
  if (joined) {
    params.push(`approvalType=${encodeURIComponent(joined)}`);
  }
  if (query.from && query.from.trim()) {
    params.push(`from=${encodeURIComponent(query.from.trim())}`);
  }
  if (query.to && query.to.trim()) {
    params.push(`to=${encodeURIComponent(query.to.trim())}`);
  }
  if (query.page !== undefined && Number.isFinite(query.page) && query.page > 0) {
    params.push(`page=${query.page}`);
  }
  if (query.pageSize !== undefined && Number.isFinite(query.pageSize) && query.pageSize > 0) {
    params.push(`pageSize=${query.pageSize}`);
  }
  return params.join('&');
}

// ================= 超时倒计时（D5 / D9） =================

/** 临近超时阈值（秒；恰好 300 = 5min 整计入「临近」，sprint-contract §3.1-D9） */
const EXPIRING_SOON_THRESHOLD_SECONDS = 300;

/**
 * D9 临近超时判定（纯函数，红色高亮依据）：
 * 统一取 Number(expiresIn) <= 300（"300" 高亮、"301" 不高亮）；
 * null / undefined / 非数字 / NaN 一律 false（无超时类型显示「—」且不高亮）。
 */
export function isExpiringSoon(expiresIn: string | null | undefined): boolean {
  const n = parseExpiresInSeconds(expiresIn);
  if (n === null) return false;
  return n <= EXPIRING_SOON_THRESHOLD_SECONDS;
}

/** expiresIn → 剩余秒数（null/空串/非法/NaN → null）；空串语义=无超时（Number('')===0 陷阱防御） */
function parseExpiresInSeconds(expiresIn: string | null | undefined): number | null {
  if (expiresIn === null || expiresIn === undefined) return null;
  const raw = String(expiresIn).trim();
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
}

/** expiresIn 无值 / 非法时的占位文案（PRD：无超时类型显示「—」） */
export const EXPIRES_IN_EMPTY_TEXT = '—';

/**
 * 倒计时展示（D5：expiresIn 为剩余秒数字符串）。
 * elapsedSeconds 为数据加载后流逝秒数（30s tick 展示递减；高亮判定不使用本参数，
 * 严格按刷新时快照值 isExpiringSoon，跨阈值变色以刷新为准——合同 E6 口径）。
 * null / 非数字 → 「—」；剩余 ≤ 0 → 「00:00」；否则 mm:ss。
 */
export function formatCountdown(
  expiresIn: string | null | undefined,
  elapsedSeconds = 0,
): string {
  const n = parseExpiresInSeconds(expiresIn);
  if (n === null) return EXPIRES_IN_EMPTY_TEXT;
  const remain = Math.max(0, Math.floor(n) - Math.max(0, Math.floor(elapsedSeconds)));
  const minutes = Math.floor(remain / 60);
  const seconds = remain % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

// ================= 决策失败分类与文案（AC-015-01/02 / 合同 E1/E2） =================

type DecisionFailureKind = 'FORBIDDEN' | 'CONFLICT' | 'FAILED';

/** 单据已被他人处理 / 已超时（先到者成功、后到者收此文案，各域幂等兜底） */
export const DECISION_CONFLICT_MESSAGE = '该单已超时或已被处理';
/** 决策接口调用失败（网络 / 服务端异常），行内按钮恢复可点 */
export const DECISION_FAILED_MESSAGE = '决策失败，请重试';
/** 403（跨商户作用域 / 无各域写权限位） */
export const DECISION_FORBIDDEN_MESSAGE = '无权限执行该操作';

/**
 * 决策失败三类映射（各域业务错误 code 透传：APR-002 4508/4509、ONB-002 4502、
 * EXP-002 4510 报销语境=状态非法；无 code 时按后端 message 文案兜底）。
 * 权限类（403/AUTH_403/「无权」）复用 corporateCore.isForbiddenError 单一实现，禁复制漂移。
 */
export function classifyDecisionFailure(
  failure: { code?: string; error?: string } | null | undefined,
): DecisionFailureKind {
  if (!failure) return 'FAILED';
  if (isForbiddenError(failure)) return 'FORBIDDEN';
  const c = String(failure.code ?? '').trim();
  if (['4502', '4508', '4509', '4510'].includes(c)) return 'CONFLICT';
  const raw = failure.error || '';
  if (!c && (raw.includes('超时') || raw.includes('已被处理') || raw.includes('状态'))) {
    return 'CONFLICT';
  }
  return 'FAILED';
}

/** 分类 → 用户文案（合同 E1/E2 定死文案，禁页面内散落硬编码） */
export function decisionFailureMessage(kind: DecisionFailureKind): string {
  if (kind === 'CONFLICT') return DECISION_CONFLICT_MESSAGE;
  if (kind === 'FORBIDDEN') return DECISION_FORBIDDEN_MESSAGE;
  return DECISION_FAILED_MESSAGE;
}

// ================= 角标计数聚合（AC-015-04） =================

/**
 * APC-002 角标计数：total 优先（数字 / 数字字符串皆可，D8 超集容错）；
 * total 缺失/非法时按 byType 五键求和兜底（缺失键记 0）。
 * 仅在数据形状完全不可解析时返回 null（调用方隐藏角标）。
 */
export function approvalBadgeCount(data: {
  total?: number | string | null;
  byType?: Record<string, number | string | null | undefined> | null;
}): number | null {
  const toCount = (v: number | string | null | undefined): number | null => {
    if (v === null || v === undefined) return null;
    const n = Number(String(v).trim());
    return Number.isFinite(n) && n >= 0 ? n : null;
  };
  const total = toCount(data.total);
  if (total !== null) return total;
  const byType = data.byType;
  if (byType && typeof byType === 'object') {
    let sum = 0;
    let seen = false;
    for (const key of Object.keys(byType)) {
      const n = toCount(byType[key]);
      if (n !== null) {
        sum += n;
        seen = true;
      }
    }
    if (seen) return sum;
  }
  return null;
}

// ================= 详情跳转目标（合同 H7 / E7） =================

type ApprovalDetailTarget = {
  path: string;
  /** false = 锚定参数缺失已降级跳对应页首（E7，不白屏） */
  anchored: boolean;
};

/**
 * 详情跳转目标构造（H7）：
 * - CORP_TX_APPROVAL → /merchant/users/corporates/:corpId/approvals
 * - CORP_EXPENSE     → /merchant/users/corporates/:corpId/billing（此两类 corpId 取自 D6）
 * - ONBOARDING       → /merchant/users/corporates/onboarding（CorporateKybReviewPage，锚定 applicationId；非 KYC 页，D7）
 * - KYC              → /merchant/users/kyc-review（锚定 caseId）
 * 锚定参数缺失 → 降级对应页首（corpId 缺失的企业侧单据降级企业用户列表页首）。
 * CORP_CARD_ISSUE 恒空集（D1）→ null（页面不渲染详情入口）。
 */
export function buildDetailTarget(item: {
  approvalType: string | null | undefined;
  corpId?: string | null;
  sourceId?: string | null;
}): ApprovalDetailTarget | null {
  const type = asApprovalType(item.approvalType);
  if (!type || type === 'CORP_CARD_ISSUE') return null;
  const corpId = (item.corpId ?? '').trim();
  const sourceId = (item.sourceId ?? '').trim();
  switch (type) {
    case 'CORP_TX_APPROVAL':
      return corpId
        ? { path: `/merchant/users/corporates/${encodeURIComponent(corpId)}/approvals`, anchored: true }
        : { path: '/merchant/users/corporates', anchored: false };
    case 'CORP_EXPENSE':
      return corpId
        ? { path: `/merchant/users/corporates/${encodeURIComponent(corpId)}/billing`, anchored: true }
        : { path: '/merchant/users/corporates', anchored: false };
    case 'ONBOARDING':
      return sourceId
        ? { path: `/merchant/users/corporates/onboarding?applicationId=${encodeURIComponent(sourceId)}`, anchored: true }
        : { path: '/merchant/users/corporates/onboarding', anchored: false };
    case 'KYC':
      return sourceId
        ? { path: `/merchant/users/kyc-review?caseId=${encodeURIComponent(sourceId)}`, anchored: true }
        : { path: '/merchant/users/kyc-review', anchored: false };
  }
}

// ================= 行唯一键与角标刷新事件 =================

/** 行唯一键：类型 + sourceId（转发决策路径参数，D6）兜底 approvalNo。 */
export function approvalRowKey(item: {
  approvalType: string | null | undefined;
  sourceId?: string | null;
  approvalNo?: string | null;
}): string {
  const type = asApprovalType(item.approvalType) ?? String(item.approvalType ?? 'UNKNOWN');
  const id = (item.sourceId ?? '').trim() || (item.approvalNo ?? '').trim() || '-';
  return `${type}:${id}`;
}

/**
 * 角标刷新广播事件（对齐 NOTICE_BADGE_EVENT 既有模式）：
 * 决策成功 / 手动刷新后 dispatch，useMerchantBadges 监听即时重拉 APC-002（AC-015-04）。
 */
export const APPROVALS_BADGE_EVENT = 'allinks-approvals-badge';
